// ** React Imports
import React, { memo, useMemo, useCallback } from "react";

// ** Third Party Components
import styled, { css } from "styled-components";

// ** Custom Optimizations
import { getCachedIconData } from "./optimization/IconCache";
import { defaultIconProps, sizeMap } from "./types/iconTypes";

// #####################################################

export const StyledIconContainer = styled.span.withConfig({
	shouldForwardProp: (prop) =>
		!prop.startsWith("$") &&
		!["customMargin", "verticalAlign", "translateY"].includes(prop),
})`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	vertical-align: -0.2em;
	position: relative;

	${({ $translateY }) =>
		$translateY &&
		css`
			transform: translateY(${$translateY});
		`}

	${({ $verticalAlign }) =>
		$verticalAlign &&
		css`
			vertical-align: ${$verticalAlign};
		`}

	${({ $customMargin }) =>
		$customMargin &&
		css`
			margin: ${$customMargin};
		`}

	svg {
		display: block;
		width: ${({ $width }) => $width || "auto"};
		height: ${({ $height, $size }) => $height || sizeMap[$size] || "1em"};

		fill: ${({ $color, theme }) => {
			if ($color === "currentColor") return "currentColor";
			if ($color && theme?.colors?.[$color]) return theme.colors[$color];
			if ($color) return $color;
			return "currentColor";
		}};

		pointer-events: ${({ $clickable }) => ($clickable ? "auto" : "none")};

		${({ $transform }) =>
			$transform &&
			css`
				transform: ${$transform};
			`}
	}
`;

// #####################################################

/**
 * BaseIcon Component - Bazowy komponent do renderowania ikon
 */
const BaseIcon = memo(
	({
		name,
		variant = defaultIconProps.variant,
		size = defaultIconProps.size,
		color = defaultIconProps.color,
		width,
		height,
		customMargin,
		verticalAlign,
		translateY,
		className,
		style,
		onClick,
		"aria-label": ariaLabel,
		"aria-hidden": ariaHidden = defaultIconProps["aria-hidden"],
		focusable = defaultIconProps.focusable,
		transform,
	}) => {
		// Memoized icon data lookup dla lepszej wydajności
		const iconData = useMemo(
			() => getCachedIconData(name, variant),
			[name, variant],
		);

		// Memoized click handler dla uniknięcia re-renders
		const handleClick = useCallback(
			(event) => {
				onClick?.(event, { name, variant });
			},
			[onClick, name, variant],
		);

		const memoizedClassName = useMemo(
			() => `icon-${name}-${variant} ${className || ""}`,
			[name, variant, className],
		);

		// #####################################################

		// Early return dla nieistniejących ikon
		if (!iconData) {
			return null;
		}

		const renderSvgContent = () => {
			if (iconData.isMultiPath && iconData.paths) {
				return iconData.paths.map((pathData, index) => {
					if (typeof pathData === "string") {
						return <path key={index} d={pathData} />;
					}
					if (pathData.d) {
						return (
							<path
								key={index}
								d={pathData.d}
								fill={pathData.fill || "currentColor"}
								stroke={pathData.stroke}
								strokeWidth={pathData.strokeWidth}
								opacity={pathData.opacity}
								{...pathData.props}
							/>
						);
					}
					return null;
				});
			}

			return <path d={iconData.path} />;
		};

		return (
			<StyledIconContainer
				className={memoizedClassName}
				style={style}
				onClick={onClick ? handleClick : undefined}
				$size={size}
				$color={color}
				$width={width}
				$height={height}
				$customMargin={customMargin}
				$verticalAlign={verticalAlign}
				$clickable={!!onClick}
				$translateY={translateY}
				$transform={transform}
			>
				<svg
					viewBox={iconData.viewBox}
					aria-label={ariaLabel}
					aria-hidden={ariaHidden}
					focusable={focusable}
					role={ariaLabel ? "img" : "presentation"}
				>
					{renderSvgContent()}
				</svg>
			</StyledIconContainer>
		);
	},
);

// #####################################################

BaseIcon.displayName = "BaseIcon";

export default BaseIcon;

// #####################################################
